import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizSearch } from "../store/actowiz/actowizSlice";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function SearchResults({ onOpenAuth, onOpenMenu }) {
  const query = useQuery();
  const navigate = useNavigate();
  const q = (query.get("q") || "").trim();

  const dispatch = useAppDispatch();
  const results = useAppSelector((s) => s.actowiz.search);
  const status = useAppSelector((s) => s.actowiz.searchStatus);
  const error = useAppSelector((s) => s.actowiz.searchError);
  const searchTotal = useAppSelector((s) => s.actowiz.searchTotal);

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const startIndex = (page - 1) * pageSize;

  useEffect(() => {
    setPage(1);
  }, [q]);

  useEffect(() => {
    if (!q) navigate("/");
  }, [q, navigate]);

  useEffect(() => {
    if (!q) return;
    dispatch(
      fetchActowizSearch({
        query: q,
        // Actowiz pagination expectation:
        // - `start` = starting index (0, 20, 40, ...)
        // - `offset` = page size (always 20 for our UI)
        start: startIndex,
        offset: pageSize,
        limit: pageSize,
        mode: "replace",
        force: true,
      })
    );
  }, [q, page, pageSize, startIndex, dispatch]);

  const visibleResults = results;
  const hasResults = q && status !== "loading" && results.length > 0;
  const hasPrev = page > 1;
  const hasNext =
    typeof searchTotal === "number"
      ? page * pageSize < searchTotal && !error && status !== "failed"
      : results.length === pageSize && !error && status !== "failed";

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex max-w-8xl gap-6 px-4 py-6 md:px-6">
        <Sidebar />

        <section className="flex-1 space-y-6">
          {!q && (
            <p className="mt-10 text-sm text-slate-500">
              Start by typing a product name in the search box above.
            </p>
          )}

          {q && status === "loading" && visibleResults.length === 0 && (
            <div className="flex justify-center py-16">
              <span className="text-sm text-slate-500">Loading…</span>
            </div>
          )}

          {q && error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
              {error}
            </div>
          )}

          {q && !error && hasResults && (
            <>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  Search results:{" "}
                  <span className="font-bold">&quot;{q}&quot;</span>
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Showing {visibleResults.length} items
                  {typeof searchTotal === "number" ? ` of ${searchTotal}` : ""}.
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 pt-2 text-xs">
                <button
                  type="button"
                  disabled={!hasPrev || status === "loading"}
                  onClick={() => setPage((v) => Math.max(1, v - 1))}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-slate-500">Page {page}</span>
                <button
                  type="button"
                  disabled={!hasNext || status === "loading"}
                  onClick={() => setPage((v) => v + 1)}
                  className="rounded-full bg-[#4F46E5] px-3 py-1.5 font-semibold text-white disabled:opacity-50"
                >
                  Next →
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {visibleResults.map((product, idx) => (
                  <ProductCard
                    key={`${product.pid || product.sku}-${idx}`}
                    accent={["orange", "blue", "green", "purple"][idx % 4]}
                    product={product}
                    showComparison={false}
                    onClick={() =>
                      navigate(
                        `/product/${encodeURIComponent(product.pid || product.sku)}`
                      )
                    }
                  />
                ))}
              </div>
            </>
          )}

          {q && !hasResults && (
            <div className="flex h-[60vh] flex-col items-center justify-center text-center">
              <div className="mb-4 text-6xl text-orange-500">⛔</div>
              <h2 className="mb-2 text-xl font-semibold text-slate-900">
                Search results: &quot;{q}&quot;
              </h2>
              <p className="text-sm text-orange-500">
                No products we found matching your selection.
              </p>
            </div>
          )}
        </section>

        <aside className="hidden w-64 rounded-2xl border border-slate-200 bg-white p-4 text-xs md:block">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">
            Your Basket
          </h2>
          <div className="space-y-2 text-[11px] text-slate-600">
            <p>No items added yet.</p>
            <p className="mt-2 flex items-center justify-between text-sm">
              <span className="font-semibold">Est. Total</span>
              <span>£0.00</span>
            </p>
            <button className="mt-3 w-full rounded-full bg-indigo-600 py-2 text-xs font-semibold text-white">
              Compare &amp; Checkout →
            </button>
          </div>
        </aside>
      </main>

      <Footer />
    </div>
  );
}

export default SearchResults;

